/** Every publisher read and write, against the endpoints the catalog and identity contexts serve. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  ActivityPage,
  ActivitySummary,
  AvailabilityRuling,
  PublishToken,
  PublishTokenSecret,
  PublishedVersion,
  PublisherNamespace,
  StoreRollup,
  VersionAvailability,
} from "@nebulize/sdk";

import { ApiError, api, request } from "@/lib/api";
import { apiBaseUrl } from "@/lib/env";

export type { ActivitySummary, PublishToken, PublishedVersion, StoreRollup, VersionAvailability };

/** What `POST /v1/activities/artifact` answers: which version it minted. */
export interface MintedVersion {
  readonly activity_id: string;
  readonly version: string;
}

const CATALOG_PAGE = 50;

export function useMyNamespace() {
  return useQuery({
    queryKey: ["publisher", "namespace"],
    retry: false,
    queryFn: async (): Promise<PublisherNamespace | null> => {
      try {
        return await api.get<PublisherNamespace>("/v1/publishers/me");
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useClaimNamespace() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (namespace: string) =>
      request<PublisherNamespace>("/v1/publishers/me/namespace", {
        method: "PUT",
        body: JSON.stringify({ namespace }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["publisher", "namespace"] });
    },
  });
}

export function useCatalog() {
  return useQuery({
    queryKey: ["publisher", "activities"],
    queryFn: () => api.get<ActivityPage>(`/v1/activities?limit=${String(CATALOG_PAGE)}`),
  });
}

export function useVersions(activityId: string | undefined) {
  return useQuery({
    queryKey: ["publisher", "activity", activityId, "versions"],
    enabled: activityId !== undefined,
    queryFn: () => api.get<PublishedVersion[]>(`/v1/activities/${activityId ?? ""}/versions`),
  });
}

export function useRollup(activityId: string | undefined) {
  return useQuery({
    queryKey: ["publisher", "activity", activityId, "rollup"],
    enabled: activityId !== undefined,
    queryFn: () => api.get<StoreRollup>(`/v1/activities/${activityId ?? ""}/rollup`),
  });
}

export interface RuleAvailability {
  readonly activityId: string;
  readonly version: string;
  readonly availability: VersionAvailability;
  readonly reason: string;
}

export function useRuleAvailability() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RuleAvailability) =>
      api.post<AvailabilityRuling>(
        `/v1/activities/${input.activityId}/versions/${input.version}/availability`,
        { availability: input.availability, reason: input.reason },
      ),
    onSuccess: (_ruling, input) => {
      void client.invalidateQueries({
        queryKey: ["publisher", "activity", input.activityId, "versions"],
      });
      void client.invalidateQueries({ queryKey: ["publisher", "activities"] });
    },
  });
}

export function usePublishTokens() {
  return useQuery({
    queryKey: ["publisher", "tokens"],
    queryFn: () => api.get<PublishToken[]>("/v1/accounts/me/publish-tokens"),
  });
}

export function useMintToken() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<PublishTokenSecret>("/v1/accounts/me/publish-tokens", { name }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["publisher", "tokens"] });
    },
  });
}

export function useRevokeToken() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) =>
      api.delete<undefined>(`/v1/accounts/me/publish-tokens/${tokenId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["publisher", "tokens"] });
    },
  });
}

/** Multipart, so it bypasses the JSON client rather than fighting its content type. */
export function usePublishArtifact() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<MintedVersion> => {
      const form = new FormData();
      form.append("artifact", file);
      const response = await fetch(`${apiBaseUrl}/v1/activities/artifact`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const shape = (typeof body === "object" && body !== null ? body : {}) as {
          code?: string;
          message?: string;
          detail?: Record<string, string>;
        };
        throw new ApiError(
          response.status,
          shape.code ?? "unknown",
          shape.message ?? response.statusText,
          shape.detail ?? {},
        );
      }
      return body as MintedVersion;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["publisher", "activities"] });
    },
  });
}
